import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

function Entry({ day, label, text, meta }) {
  if (!text || !text.trim()) return null
  return (
    <div style={{ marginBottom: '0.85rem', paddingBottom: '0.85rem', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#00712D', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {day != null ? `Day ${day}` : ''} {label}
        </span>
        {meta && <span style={{ fontSize: '0.65rem', color: '#aaa' }}>{meta}</span>}
      </div>
      <p style={{ fontSize: '0.85rem', color: '#1A1A1A', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{text}</p>
    </div>
  )
}

export default function SearchJournal({ session, profile, onBack }) {
  const [loading, setLoading] = useState(true)
  const [responses, setResponses] = useState([])
  const [notes, setNotes] = useState([])
  const [contactNotes, setContactNotes] = useState([])
  const [filter, setFilter] = useState('all') // 'all' | 'claude' | 'notes' | 'foundation' | 'people'

  useEffect(() => { load() }, [session?.user?.id])

  async function load() {
    if (!session?.user?.id) { setLoading(false); return }
    setLoading(true)
    const [{ data: r }, { data: p }, { data: hotTen }] = await Promise.all([
      supabase.from('daily_responses').select('day_number, prompt_index, content, updated_at').eq('user_id', session.user.id).order('day_number', { ascending: true }),
      supabase.from('daily_progress').select('day_number, step_notes').eq('user_id', session.user.id).order('day_number', { ascending: true }),
      supabase.from('hot_ten').select('name, company, notes, last_contacted_at').eq('user_id', session.user.id),
    ])
    setResponses(r || [])
    setContactNotes((hotTen || []).filter(c => c.notes && c.notes.trim()))
    // Flatten step_notes { "0": "text", "2": "text" } into individual entries per day
    const flatNotes = []
    for (const row of (p || [])) {
      const stepNotes = row.step_notes || {}
      for (const [idx, text] of Object.entries(stepNotes)) {
        if (text && text.trim()) flatNotes.push({ day: row.day_number, stepIndex: idx, text })
      }
    }
    setNotes(flatNotes)
    setLoading(false)
  }

  const foundationEntries = [
    { label: 'What energizes me', value: profile?.what_you_love },
    { label: "What I'm genuinely great at", value: profile?.what_youre_good_at },
    { label: 'What impact I want', value: profile?.what_world_needs },
    { label: 'Start / Stop / Keep', value: profile?.start_stop_keep },
    { label: 'Target titles', value: profile?.target_titles },
    { label: 'Non-negotiables', value: profile?.non_negotiables },
    { label: 'Deal-breakers', value: profile?.deal_breakers },
  ].filter(e => e.value && e.value.trim())

  const hasAnything = foundationEntries.length > 0 || responses.length > 0 || notes.length > 0 || contactNotes.length > 0

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#aaa', fontSize: '0.85rem' }}>Loading your journal...</div>

  return (
    <div>
      {onBack && (
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#00712D', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '0.85rem' }}>
          ← Back to My Search
        </button>
      )}
      <h2 className="section-title" style={{ marginBottom: '0.25rem' }}>Your Search Journal</h2>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem', lineHeight: 1.5 }}>
        Everything you've written across your search, compiled in one place — your foundation work, notes from real conversations, and what Claude gave you along the way.
      </p>

      {!hasAnything && (
        <div className="card" style={{ background: '#F5F5F5', border: '1px dashed #ccc', textAlign: 'center', padding: '1.5rem' }}>
          <div style={{ fontSize: '0.85rem', color: '#888' }}>Nothing saved yet. As you complete daily steps, add notes, and save AI responses, they'll show up here automatically.</div>
        </div>
      )}

      {/* Filter tabs */}
      {hasAnything && (
        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', overflowX: 'auto' }}>
          {[
            { id: 'all', label: 'All' },
            { id: 'foundation', label: 'Foundation' },
            { id: 'notes', label: 'My Notes' },
            { id: 'people', label: 'People' },
            { id: 'claude', label: 'From Claude' },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{ padding: '0.35rem 0.75rem', borderRadius: '20px', border: `1px solid ${filter === f.id ? '#00712D' : '#e8e8e8'}`, background: filter === f.id ? '#00712D' : '#fff', color: filter === f.id ? '#fff' : '#555', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      {(filter === 'all' || filter === 'foundation') && foundationEntries.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
            👤 Your Foundation
          </div>
          {foundationEntries.map(e => <Entry key={e.label} label={e.label} text={e.value} />)}
        </div>
      )}

      {(filter === 'all' || filter === 'notes') && notes.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
            📝 My Notes
          </div>
          {notes.map((n, i) => <Entry key={i} day={n.day} label="" text={n.text} />)}
        </div>
      )}

      {(filter === 'all' || filter === 'people') && contactNotes.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
            👥 People
          </div>
          {contactNotes.map((c, i) => (
            <Entry key={i} label={`${c.name}${c.company ? ` · ${c.company}` : ''}`} text={c.notes} meta={c.last_contacted_at ? new Date(c.last_contacted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''} />
          ))}
        </div>
      )}

      {(filter === 'all' || filter === 'claude') && responses.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
            💡 From Claude
          </div>
          {responses.map((r, i) => (
            <Entry key={i} day={r.day_number} label={`Prompt ${r.prompt_index + 1}`} text={r.content} meta={r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''} />
          ))}
        </div>
      )}
    </div>
  )
}
